<script setup>
import { cloneDeep, isEmpty, isEqual, merge } from "lodash";
import { computed, onMounted, reactive, ref, unref, watch } from "vue";
import { AisConfigure, AisStateResults } from "vue-instantsearch/vue3/es";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { INITIAL_FILTERS } from "@/components/ExperiencePage/Algolia/AlgoliaSideBar/constants";
import { useSearchClient } from "@/components/ExperiencePage/Algolia/useSearchClient";
import { buildFilterStrings2 } from "@/components/ExperiencePage/Algolia/utils";
import ApplicationExistsModal from "@/components/ExperiencePage/ApplicationExistsModal.vue";
import HomeSessionCard2 from "@/components/ExperiencePage/Sessions/Components/SessionCard2.vue";
import Spinner from "@/components/helpers/Spinner.vue";
import NoResultsPage from "@/components/NoResultsPage.vue";
import { API_ENTITY_INFORMATION } from "@/constants";
import ArrowRightIcon from "../Menu/ArrowRightIcon.vue";
import { displayNewCardDesign, useApplicationProgress, useConvertToUrlParams } from "./composables";
import { ALGOLIA_LOADING_STATUSES, algoliaConfigurationMap, TAB_ROUTE_NAMES, TABS } from "./constants";
import QuickFilter from "./QuickFilter";
import HomeSessionCard from "./ResultCards/HomeSessionCard";
import { selectImageForProgram } from "./Tabs/utils";
import AlgoliaEmptyStateLoader from "./widgets/AlgoliaEmptyStateLoader.vue";

const { searchClient } = useSearchClient();

const store = useStore();
const router = useRouter();

const programImageIndices = {};
const algoliaFilters = ref("");

const domainEntityId = computed(() => store.getters.getDomainEntityId || API_ENTITY_INFORMATION.id);

const tenancyDomainRestrictedMap = {
  sessionsDomainRestrictedId: domainEntityId.value,
  programsDomainRestrictedId: domainEntityId.value,
  programsPageOwnerEntityIdFilter: domainEntityId.value,
  sessionsOwnerEntityIdFilter: domainEntityId.value,
  classesVisibility: domainEntityId.value,
};

const componentState = reactive({
  filters: {
    ...INITIAL_FILTERS,
  },
});

const oktaId = computed(() => {
  return store.state.currentUser?.participantId ?? "";
});

const profileData = computed(() => {
  return store.state.profileData;
});

const activeAlgoliaIndex = computed(() => {
  const currentTab = TABS[0];
  return currentTab ? currentTab.algoliaIndex : undefined;
});

const currentSearchIndex = computed(() => {
  return unref(searchClient).initIndex(unref(activeAlgoliaIndex));
});

const algoliaConfiguration = computed(() => {
  return algoliaConfigurationMap[TABS[0].value] || {};
});

const updateFilters = (clonedFilters) => {
  const combinedTenancyFilters = merge(clonedFilters, tenancyDomainRestrictedMap);
  if (clonedFilters) {
    algoliaFilters.value = buildFilterStrings2(unref(algoliaConfiguration), combinedTenancyFilters);
  }
};

const redirectToViewPage = (programId, sessionId) => {
  router.push({
    name: "program-detail-view",
    params: { programPageId: programId },
    query: { "session-id": sessionId },
  });
};

const onSubmit = () => {
  const query = useConvertToUrlParams(componentState.filters);
  const pathName =
    componentState?.filters?.programTypes?.length === 1 && componentState?.filters?.programTypes?.[0] === "Internships"
      ? TAB_ROUTE_NAMES.INTERNSHIP_POSTINGS
      : TAB_ROUTE_NAMES.PROGRAMS;
  if (!isEmpty(query)) {
    router.push({ name: pathName, query });
  } else {
    router.push({ path: pathName });
  }
};

const { onApplyClicked, showApplicationTakenModal } = useApplicationProgress();

watch(
  () => componentState.filters,
  (newFilters, oldFilters) => {
    if (!isEqual(newFilters, oldFilters)) {
      const clonedFilters = cloneDeep(newFilters);
      updateFilters(clonedFilters);
    }
  },
  { deep: true, immediate: true },
);

onMounted(async () => {
  if (oktaId.value && isEmpty(profileData.value)) {
    await store.dispatch("getProfile", oktaId.value);
  }
  if (store.state.currentUser) {
    await store.dispatch("getStudentApplications");
  }
  if (isEmpty(store.state.billingRules)) {
    store.dispatch("getBillingRules");
  }
});

const filterAdded = (state) => {
  let retVal = false;
  if (state?.disjunctiveFacetsRefinements) {
    for (const key in state.disjunctiveFacetsRefinements) {
      if (Object.hasOwn(state.disjunctiveFacetsRefinements, key)) {
        const disjunctiveFacetsRefinement = state.disjunctiveFacetsRefinements[key];
        if (disjunctiveFacetsRefinement?.length > 0) {
          retVal = true;
          break;
        }
      }
    }
  }
  return retVal;
};

const searchFunction = (helper) => {
  const isFilterAdded = filterAdded(helper?.state);
  if (!isFilterAdded) {
    helper.search();
  }
};
</script>

<template>
  <ais-instant-search
    :index-name="activeAlgoliaIndex"
    :search-client="searchClient"
    :search-function="searchFunction"
  >
    <ais-configure :filters="algoliaFilters" :hits-per-page.camel="8" />
    <ApplicationExistsModal
      :show-application-taken-modal="showApplicationTakenModal"
      @close="showApplicationTakenModal = false"
    />
    <QuickFilter
      v-model="componentState.filters"
      :algolia-configuration="algoliaConfigurationMap.programs"
      :current-search-index="currentSearchIndex"
      @search-submitted="onSubmit"
    />
    <div id="landingPageContent" class="px-2.5 bg-white mb-36 mt-4-625">
      <div
        id="landingPageHeader"
        class="flex flex-col items-start md:flex-row md:justify-between md:mb-12"
      >
        <h2
          id="trendingProgramsTitle"
          class="font-montserrat non-italic font-bold text-indigo-base uppercase leading-9 text-xl sm:text-2.5xl mb-5 md:mb-0"
        >
          TRENDING PROGRAMS
        </h2>
        <button
          id="browseAllProgramsButton"
          :class="`flex flex-row justify-center items-center ${secondaryColorClass} gap-x-3 font-bold uppercase text-base mb-9 md:mb-0`"
          type="button"
          @click="$router.push({ name: 'results' })"
        >
          <span class="leading-none">Browse All Programs</span>
          <ArrowRightIcon :fill-color="`${secondaryColor}`" />
        </button>
      </div>
      <AlgoliaEmptyStateLoader />
      <ais-state-results>
        <template v-slot="{ status, error, results }">
          <div
            v-if="ALGOLIA_LOADING_STATUSES.includes(status)"
            class="relative h-50-screen"
          >
            <Spinner />
          </div>
          <div v-else-if="results?.hits?.length > 0">
            <div
              v-if="!displayNewCardDesign"
              class="justify-items-center grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12 lg:px-4 pb-18"
            >
              <HomeSessionCard
                v-for="(program, index) in results?.hits"
                :key="index"
                :image-url="selectImageForProgram(program, programImageIndices)"
                :program="program"
                class="grid-cols-1"
                event-category="HP Personalize CTA"
                @view-clicked="
                  redirectToViewPage(program?.program_page?.id, program?.session?.id)
                "
                @apply-clicked="onApplyClicked(program)"
                :domainEntityId="domainEntityId"
              />
            </div>
            <div
              v-else
              class="justify-items-center grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12 lg:px-4 pb-18"
            >
              <HomeSessionCard2
                v-for="(program, index) in results?.hits"
                :key="index"
                :main-container-classes="'flex flex-col items-stretch max-w-25r font-montserrat'"
                :vertical-style="true"
                :session-data="program"
                :image-url="selectImageForProgram(program, programImageIndices)"
                @view-clicked="
                  redirectToViewPage(program?.program_page?.id, program?.session?.id)
                "
                @apply-clicked="onApplyClicked(program)"
              />
            </div>
          </div>
          <div v-else>
            <NoResultsPage margin-top="mt-5" />
          </div>
        </template>
      </ais-state-results>
      <div
        id="browseAllProgramsButtonBottom"
        class="flex justify-center items-center mt-11 sm:mt-24"
      >
        <button
          :class="`flex flex-row justify-center items-center ${secondaryColorClass} gap-x-3 font-bold uppercase text-base mb-9 md:mb-0`"
          type="button"
          @click="$router.push({ name: 'results' })"
        >
          <span class="leading-none">Browse All Programs</span>
          <ArrowRightIcon :fill-color="`${secondaryColor}`" />
        </button>
      </div>
    </div>
  </ais-instant-search>
</template>
